import type { AuthSessionView, SignInRequest } from "@dynamic-rag/contracts";
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { api, isSignedOut, unwrap } from "./client";

/** Why the sign-in page is shown: the session was ended on the server, or the user signed out.
 * Null on a first visit. */
export type SignedOutReason = "ended" | "signed-out";

export type SessionState =
  | { status: "signed-in"; session: AuthSessionView }
  | { status: "signed-out"; reason: SignedOutReason | null };

const SIGNED_IN_HINT = "qs-signed-in";

/** Whether this browser was signed in the last time the server said. Only a hint, never trusted:
 * the landing page uses it to show itself at once to visitors, and to wait for the server's answer
 * only for someone who was signed in, so they never see it flash before Chat. */
export function wasSignedIn(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT) === "1";
  } catch {
    return false;
  }
}

function rememberSignedIn(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(SIGNED_IN_HINT, "1");
    else window.localStorage.removeItem(SIGNED_IN_HINT);
  } catch {
    // Storage can be blocked; the page then simply waits for the server's answer.
  }
}

async function fetchSession(): Promise<SessionState> {
  try {
    const session = await unwrap(api.GET("/api/v1/auth/session"));
    rememberSignedIn(true);
    return { status: "signed-in", session };
  } catch (error) {
    if (isSignedOut(error)) {
      rememberSignedIn(false);
      return { status: "signed-out", reason: error.code === "SESSION_ENDED" ? "ended" : null };
    }
    throw error;
  }
}

/** Asked once per page load. Signing in or out, or a request that finds the session gone
 * (`onSignedOut`), changes it; the server checks the session on every request anyway. */
export const sessionQuery = queryOptions({
  queryKey: ["auth", "session"] as const,
  queryFn: fetchSession,
  staleTime: Infinity,
});

export function useSession() {
  return useQuery(sessionQuery);
}

/** Whether the signed-in account has every page: the admin, or anyone where sign-in is off. An
 * account with the user role has the Chat page only. The server enforces this on every request;
 * this only decides what the screen shows. */
export function useIsAdmin(): boolean {
  const session = useSession();
  return session.data?.status === "signed-in" && session.data.session.role !== "user";
}

function setSession(client: QueryClient, state: SessionState): void {
  rememberSignedIn(state.status === "signed-in");
  client.setQueryData(sessionQuery.queryKey, state);
}

export function markSignedOut(client: QueryClient, reason: SignedOutReason): void {
  setSession(client, { status: "signed-out", reason });
}

/** Signing in sets the session cookie (HttpOnly: this code never sees it). */
export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SignInRequest) => unwrap(api.POST("/api/v1/auth/login", { body })),
    onSuccess: (session) => setSession(client, { status: "signed-in", session }),
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/auth/logout")),
    onSuccess: () => markSignedOut(client, "signed-out"),
  });
}

/** Where a page goes after signing in: a page of this app, never another site ("//host"). The
 * chat opens new after signing in (22 Oct 2026): a conversation in the address (?c=) is left
 * out; it is in History. */
export function safeNext(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return "/chat";
  if (value.startsWith("/login")) return "/chat";
  return value.split(/[?#]/)[0] === "/chat" ? "/chat" : value;
}
